"use client";

import * as React from "react";
import { Direction } from "radix-ui";

/**
 * #415 — make a data table cost ONE tab stop instead of one per control.
 *
 * THE PROBLEM. A table of editable cells is tabbable in proportion to its data.
 * Measured on /recipients: 25 stops in the header and ~16 per row — Select, the
 * status toggle, then a Copy + Edit pair for almost every column, a groups
 * link, Profile %, View details, More actions. 301 stops on a default page,
 * roughly 1,900 at 100 rows per page. A keyboard user who wants the pagination
 * controls below the table traverses every one of them. Tidying individual
 * columns cannot fix that: the count grows with the rows, and it is identical
 * on all 183 tables that render UnifiedDataTable.
 *
 * THE PATTERN. WAI-ARIA `grid` with a roving tabindex — the standard answer,
 * and what a screen-reader user expects of a data grid:
 *
 *   - The grid is a single tab stop. Exactly one cell carries tabindex="0";
 *     every other cell and every control inside the body carries "-1".
 *   - Arrow keys move the active cell. Home/End jump within the row,
 *     Ctrl+Home/End to the grid's corners. Left and Right follow the reading
 *     direction: in right-to-left (Arabic) the next column is drawn to the
 *     left, so ArrowLeft moves to it (RTL-05). Home/End stay logical.
 *   - Enter (or F2) steps INTO the active cell: its own controls become
 *     tabbable so Tab still cycles the Copy/Edit pair inside that one cell.
 *   - Escape steps back out to the cell, so a user is never trapped.
 *   - Tab from the grid leaves it entirely, because nothing else inside is
 *     tabbable. That is the whole point.
 *
 * WHY IT GENERALISES. It reads the rendered DOM — `tbody tr > td` and whatever
 * happens to be focusable inside — never a column definition. A consumer can
 * put anything in a cell and still get the behaviour.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It never touches the header, the toolbar or
 * the footer: those are a handful of stops and they are where a Tab user
 * expects to land. And it yields entirely while a menu, dialog or listbox has
 * focus — those widgets own their own keyboard contract, and fighting them is
 * how roving-tabindex implementations usually break.
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** Widgets that own their own keyboard handling while open. */
const NESTED_WIDGET_SELECTOR = '[role="menu"],[role="dialog"],[role="listbox"]';

type CellPosition = { row: number; col: number };

function readGrid(table: HTMLTableElement): HTMLTableCellElement[][] {
  return Array.from(table.querySelectorAll<HTMLTableRowElement>("tbody tr"))
    .map((tr) => Array.from(tr.querySelectorAll<HTMLTableCellElement>("td")))
    .filter((cells) => cells.length > 0);
}

function focusablesIn(cell: HTMLElement): HTMLElement[] {
  return Array.from(cell.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export function useGridKeyboardNavigation(
  tableRef: React.RefObject<HTMLTableElement | null>,
  { enabled }: { enabled: boolean },
) {
  // The active cell survives re-renders; TanStack rebuilds rows constantly and
  // a state-held position would fight it.
  const activeRef = React.useRef<CellPosition>({ row: 0, col: 0 });
  // True while the user has stepped INTO a cell (Enter/F2), so the re-apply
  // pass does not yank tabbability back out from under them mid-interaction.
  const insideCellRef = React.useRef(false);
  // The column step of ArrowRight: +1 left-to-right, -1 right-to-left, where
  // the next column is drawn to the left. Read from the same Radix direction
  // provider the i18n provider feeds (lib/i18n/provider.tsx).
  const rightStep = Direction.useDirection() === "rtl" ? -1 : 1;

  /**
   * Re-assert the roving state. Runs after every commit — React re-renders
   * rows on sort, filter, page and selection changes, and each of those
   * replaces the nodes we annotated.
   */
  const applyRovingTabIndex = React.useCallback(() => {
    const table = tableRef.current;
    if (!table || !enabled) return;

    const grid = readGrid(table);
    if (grid.length === 0) return;

    // Clamp — the row count changes on every page turn and filter.
    const active = activeRef.current;
    const row = Math.min(active.row, grid.length - 1);
    const col = Math.min(active.col, grid[row].length - 1);
    activeRef.current = { row, col };

    // The ROW itself is focusable whenever a consumer wires `rowBehavior.onClick`
    // (TanStack rows render tabIndex={0} so a keyboard user can activate them).
    // Left alone that is one stop per row — 15 of the 16 that survived the first
    // pass of this sweep. Row activation is not lost: Enter on any cell in the
    // row still triggers the row's own handler through normal bubbling.
    for (const tr of table.querySelectorAll<HTMLTableRowElement>("tbody tr")) {
      if (tr.hasAttribute("tabindex")) tr.tabIndex = -1;
    }

    grid.forEach((cells, r) => {
      cells.forEach((cell, c) => {
        const isActive = r === row && c === col;
        cell.tabIndex = isActive ? 0 : -1;
        const inside = isActive && insideCellRef.current;
        for (const el of focusablesIn(cell)) {
          // Only the controls of a cell the user has stepped into stay
          // tabbable, so Tab still cycles a Copy/Edit pair within that cell.
          el.tabIndex = inside ? 0 : -1;
        }
      });
    });
  }, [tableRef, enabled]);

  // No dependency array on purpose: this must re-assert after every commit.
  React.useEffect(() => {
    applyRovingTabIndex();
  });

  // Rows can also change outside a React commit (a portalled cell editor
  // closing, an async cell). Observing the body catches those.
  React.useEffect(() => {
    const table = tableRef.current;
    if (!table || !enabled) return;
    const body = table.querySelector("tbody");
    if (!body) return;

    const observer = new MutationObserver(() => applyRovingTabIndex());
    observer.observe(body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [tableRef, enabled, applyRovingTabIndex]);

  const moveTo = React.useCallback(
    (row: number, col: number) => {
      const table = tableRef.current;
      if (!table) return;
      const grid = readGrid(table);
      if (grid.length === 0) return;

      const r = Math.max(0, Math.min(row, grid.length - 1));
      const c = Math.max(0, Math.min(col, grid[r].length - 1));

      insideCellRef.current = false;
      activeRef.current = { row: r, col: c };
      applyRovingTabIndex();
      grid[r][c].focus();
    },
    [tableRef, applyRovingTabIndex],
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTableElement>) => {
      if (!enabled) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Yield to an open menu / dialog / listbox — it owns its own keys.
      if (target.closest(NESTED_WIDGET_SELECTOR)) return;

      const table = tableRef.current;
      if (!table) return;

      const cell = target.closest("td");
      if (!cell || !table.contains(cell)) return;

      const grid = readGrid(table);
      const row = grid.findIndex((cells) => cells.includes(cell));
      if (row === -1) return;
      const col = grid[row].indexOf(cell);
      const onCellItself = target === cell;

      switch (event.key) {
        case "ArrowRight":
          if (!onCellItself) return; // inside a cell: let the control have it
          event.preventDefault();
          moveTo(row, col + rightStep);
          return;
        case "ArrowLeft":
          if (!onCellItself) return;
          event.preventDefault();
          moveTo(row, col - rightStep);
          return;
        case "ArrowDown":
          if (!onCellItself) return;
          event.preventDefault();
          moveTo(row + 1, col);
          return;
        case "ArrowUp":
          if (!onCellItself) return;
          event.preventDefault();
          moveTo(row - 1, col);
          return;
        case "Home":
          if (!onCellItself) return;
          event.preventDefault();
          moveTo(event.ctrlKey ? 0 : row, 0);
          return;
        case "End":
          if (!onCellItself) return;
          event.preventDefault();
          moveTo(
            event.ctrlKey ? grid.length - 1 : row,
            grid[row].length - 1,
          );
          return;
        case "Enter":
        case "F2": {
          if (!onCellItself) return;
          const [first] = focusablesIn(cell);
          if (!first) return; // a text-only cell has nothing to step into
          event.preventDefault();
          insideCellRef.current = true;
          applyRovingTabIndex();
          first.focus();
          return;
        }
        case "Escape": {
          if (onCellItself) return; // already out
          event.preventDefault();
          insideCellRef.current = false;
          applyRovingTabIndex();
          cell.focus();
          return;
        }
        default:
          return;
      }
    },
    [enabled, tableRef, moveTo, applyRovingTabIndex, rightStep],
  );

  /**
   * A pointer user clicking a cell should move the roving position there, or
   * the next arrow press jumps back to wherever the keyboard left off.
   */
  const onFocusCapture = React.useCallback(
    (event: React.FocusEvent<HTMLTableElement>) => {
      if (!enabled) return;
      const table = tableRef.current;
      if (!table) return;
      const focused = event.target as unknown as HTMLElement;
      const cell = focused.closest("td");
      if (!cell || !table.contains(cell)) return;

      const grid = readGrid(table);
      const row = grid.findIndex((cells) => cells.includes(cell));
      if (row === -1) return;
      const col = grid[row].indexOf(cell);
      if (row === activeRef.current.row && col === activeRef.current.col) return;

      activeRef.current = { row, col };
      insideCellRef.current = focused !== cell;
      applyRovingTabIndex();
    },
    [enabled, tableRef, applyRovingTabIndex],
  );

  return { onKeyDown, onFocusCapture };
}
